import { useScrollToTop } from "@/hooks/useScrollToTop";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card } from "@/components/ui/card";

const ShippingPolicy = () => {
  useScrollToTop();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Shipping & Delivery Policy | Aureum VII Trading"
        description="Shipping and delivery information for luxury smart toilets from Aureum VII Trading. European delivery, freight options, and installation services."
        keywords="shipping policy, delivery, freight, installation, smart toilet delivery"
        url="/shipping-policy"
      />
      <Navigation />
      
      <main className="container mx-auto px-4 py-20 max-w-4xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-8 text-center">Shipping & Delivery Policy</h1>
        
        <Card className="p-8 space-y-8">
          <section>
            <p className="text-muted-foreground mb-6">
              Last Updated: {new Date().toLocaleDateString('en-GB')}
            </p>
            <p className="mb-6">
              Aureum VII Trading is committed to delivering your luxury smart toilets and bathroom technology 
              safely and efficiently across Europe. This Shipping & Delivery Policy outlines our shipping 
              methods, delivery timelines, costs, and procedures.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">1. Shipping Coverage</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">1.1 Primary Markets</h3>
            <p className="mb-4">We ship to the following countries:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Spain:</strong> All regions including mainland, Balearic Islands, and Canary Islands</li>
              <li><strong>Germany:</strong> All states (Bundesländer)</li>
              <li><strong>France:</strong> Metropolitan France and major overseas territories</li>
              <li><strong>Italy:</strong> All regions including islands</li>
              <li><strong>United Kingdom:</strong> England, Scotland, Wales, Northern Ireland</li>
              <li><strong>Portugal:</strong> Mainland and Madeira</li>
              <li><strong>Netherlands:</strong> All provinces</li>
              <li><strong>Belgium:</strong> All regions</li>
              <li><strong>Austria:</strong> All states</li>
              <li><strong>Switzerland:</strong> All cantons (additional customs fees may apply)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">1.2 Other European Countries</h3>
            <p className="mb-4">
              We can arrange shipping to other European countries on request. Please contact us at 
              <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline ml-1">
                info@aureum7trading.com
              </a> for availability and quotes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Shipping Methods</h2>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6 rounded">
              <h3 className="text-lg font-bold mb-2 text-amber-900">🚢 Delivery Time Overview</h3>
              <p className="text-amber-800 mb-3">
                We offer two primary shipping methods tailored to your order volume and urgency:
              </p>
              <ul className="space-y-2 text-amber-900">
                <li><strong>Bulk Orders (Freight Shipping):</strong> Up to 40 days delivery time</li>
                <li><strong>Smaller Orders (Air Freight):</strong> 10-15 days expedited delivery</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.1 Freight Shipping (Bulk Orders)</h3>
            <p className="mb-4">
              <strong>Best for:</strong> Large orders, bulk purchases, commercial projects<br />
              <strong>Delivery Time:</strong> Up to 40 days<br />
              <strong>Tracking:</strong> Full tracking provided with regular status updates<br />
              <strong>Insurance:</strong> Fully insured door-to-door<br />
              <strong>Cost:</strong> Most economical option for bulk quantities
            </p>
            <p className="mb-4">
              Freight shipping is ideal for contractors, businesses, and customers ordering multiple units. 
              Products are professionally palletized, containerized, and shipped via trusted freight carriers. 
              This method offers the best value for larger orders where delivery time is flexible.
            </p>
            <p className="mb-4 text-sm text-muted-foreground">
              💡 <em>We keep you informed every step of the way with shipment tracking and proactive status updates.</em>
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.2 Air Freight (Smaller Orders - Expedited)</h3>
            <p className="mb-4">
              <strong>Best for:</strong> Individual units, urgent needs, residential customers<br />
              <strong>Delivery Time:</strong> 10-15 days<br />
              <strong>Tracking:</strong> Real-time tracking with detailed updates<br />
              <strong>Insurance:</strong> Fully insured<br />
              <strong>Availability:</strong> Available upon request
            </p>
            <p className="mb-4">
              For customers who need their smart toilet system faster, we offer expedited air freight shipping. 
              This premium service significantly reduces delivery time while maintaining our high standards for 
              safe, secure transport. Perfect for renovation projects with tight deadlines or customers eager to 
              upgrade their bathroom experience quickly.
            </p>
            <p className="mb-4 text-sm text-muted-foreground">
              ✈️ <em>Contact us to confirm air freight availability and pricing for your specific order and destination.</em>
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.3 White Glove Delivery Service</h3>
            <p className="mb-4">
              <strong>Delivery Time:</strong> Scheduled appointment after arrival<br />
              <strong>Services Included:</strong> Inside delivery, unpacking, placement, debris removal<br />
              <strong>Additional Cost:</strong> Varies by location<br />
              <strong>Availability:</strong> Select regions
            </p>
            <p className="mb-4">
              White Glove delivery includes professional handlers who will deliver the product to the room 
              of your choice, carefully unpack it, and remove all packaging materials. This premium service 
              ensures your luxury smart toilet arrives in perfect condition exactly where you need it. 
              Professional installation is arranged separately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Delivery Timelines & Communication</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">3.1 Processing Time</h3>
            <p className="mb-4">
              Orders are typically processed within 1-3 business days after payment confirmation. Custom 
              orders or special configurations may require 2-4 weeks processing time. We'll notify you 
              immediately once your order enters production and again when it ships.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.2 Complete Delivery Timeline by Order Type</h3>
            
            <div className="space-y-6 mb-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-bold text-lg mb-2">🚢 Bulk Orders (Freight Shipping)</h4>
                <p className="mb-3"><strong>Total Delivery Time:</strong> Up to 40 days from order confirmation</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Processing & Preparation: 3-7 days</li>
                  <li>Container Freight Transit: 25-35 days</li>
                  <li>Customs Clearance: 1-3 days (EU destinations)</li>
                  <li>Local Delivery to Your Address: 2-5 days</li>
                </ul>
                <p className="mt-3 text-sm text-muted-foreground">
                  <strong>Best for:</strong> Large orders, commercial projects, bulk purchases where delivery flexibility 
                  allows for maximum cost savings.
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-bold text-lg mb-2">✈️ Smaller Orders (Air Freight - Expedited)</h4>
                <p className="mb-3"><strong>Total Delivery Time:</strong> 10-15 days from order confirmation</p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Processing & Preparation: 1-3 days</li>
                  <li>Air Freight Transit: 5-8 days</li>
                  <li>Customs Clearance: 1-2 days (EU destinations)</li>
                  <li>Express Delivery to Your Address: 1-2 days</li>
                </ul>
                <p className="mt-3 text-sm text-muted-foreground">
                  <strong>Best for:</strong> Individual units, residential customers, urgent renovation projects, 
                  or anyone who wants their luxury bathroom upgrade sooner.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.3 Regional Considerations</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Spain (mainland):</strong> Standard freight regions</li>
              <li><strong>Spain (islands):</strong> May require additional 2-3 days for island delivery</li>
              <li><strong>Germany, France, Italy:</strong> Full coverage with both freight and air options</li>
              <li><strong>United Kingdom:</strong> Post-Brexit customs may add 1-2 days (duties customer responsibility)</li>
              <li><strong>Other EU countries:</strong> Most locations served via both shipping methods</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">3.4 Our Communication Commitment</h3>
            <p className="mb-4">
              We believe in complete transparency throughout the shipping process. Here's what you can expect:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Order Confirmation:</strong> Immediate email with estimated delivery timeframe</li>
              <li><strong>Production Updates:</strong> Notification when your order enters manufacturing</li>
              <li><strong>Shipment Alert:</strong> Full tracking information when your order ships</li>
              <li><strong>In-Transit Updates:</strong> Regular status updates for long-distance shipments</li>
              <li><strong>Delivery Scheduling:</strong> 24-48 hour advance notice to arrange convenient delivery</li>
              <li><strong>Arrival Confirmation:</strong> Follow-up to ensure successful delivery</li>
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">
              *Delivery times are estimates based on normal conditions. Delays may occur due to customs, carrier 
              issues, weather, or force majeure events. We'll keep you informed of any changes to your expected delivery date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Shipping Costs</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">4.1 Standard Shipping Rates</h3>
            <p className="mb-4">
              Shipping costs are calculated based on product weight, size, destination, and delivery method. 
              Exact costs are displayed at checkout before you complete your order.
            </p>
            <p className="mb-4">Typical ranges:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Spain (mainland):</strong> Confirmed by quotation</li>
              <li><strong>Germany, France, Italy:</strong> Confirmed by quotation</li>
              <li><strong>UK, Portugal, Netherlands:</strong> Confirmed by quotation</li>
              <li><strong>Other EU countries:</strong> Confirmed by quotation</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.2 Free Shipping Promotions</h3>
            <p className="mb-4">
              We occasionally offer free or discounted shipping promotions. Check our website or sign up for 
              our newsletter to receive notifications about special offers.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">4.3 Bulk Orders</h3>
            <p className="mb-4">
              Contractors, architects, and businesses ordering multiple units may qualify for discounted 
              shipping rates. Contact our B2B sales team at 
              <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline ml-1">
                info@aureum7trading.com
              </a> for quotes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Delivery Process</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">5.1 Order Confirmation</h3>
            <p className="mb-4">
              Upon order placement, you will receive an email confirmation with order details and estimated 
              delivery timeframe.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.2 Shipment Notification</h3>
            <p className="mb-4">
              Once your order ships, you will receive an email with tracking information and a link to monitor 
              your shipment in real-time.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.3 Delivery Appointment</h3>
            <p className="mb-4">
              For freight deliveries, the carrier will contact you 24-48 hours before delivery to schedule a 
              convenient delivery window. Please ensure someone is available to receive the shipment.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">5.4 Delivery Requirements</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Adult signature (18+ years) required at delivery</li>
              <li>Clear access to delivery location (stairs, elevators, doorways)</li>
              <li>Assistance may be required for heavy items</li>
              <li>Delivery is curbside unless White Glove service is selected</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Inspection Upon Delivery</h2>
            <p className="mb-4">
              <strong>IMPORTANT:</strong> Inspect packages immediately upon delivery. Check for visible damage 
              to boxes, crates, or products. If damage is evident:
            </p>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Note damage on the delivery receipt before signing</li>
              <li>Take photos of damaged packaging and products</li>
              <li>Contact us within 48 hours at info@aureum7trading.com</li>
              <li>Do not discard damaged packaging until claim is resolved</li>
            </ol>
            <p className="mt-4">
              Failure to report damage within 48 hours may affect your ability to file a claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Refused or Undeliverable Shipments</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">7.1 Failed Delivery Attempts</h3>
            <p className="mb-4">
              If the carrier cannot deliver due to inaccessibility, refused delivery, or no one available to 
              sign, the shipment will be returned to our warehouse. You will be responsible for additional 
              shipping costs for redelivery.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">7.2 Incorrect Address</h3>
            <p className="mb-4">
              If an order is returned due to an incorrect or incomplete address provided by the customer, 
              reshipping fees will apply. Please verify your shipping address carefully before completing 
              your order.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. International Shipments & Customs</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">8.1 Customs Duties & Taxes</h3>
            <p className="mb-4">
              <strong>EU Countries:</strong> No additional customs duties for shipments within the EU.<br />
              <strong>UK (Post-Brexit):</strong> Customs duties and VAT may apply. Customers are responsible 
              for these fees.<br />
              <strong>Switzerland & Other Non-EU Countries:</strong> Customs duties, import taxes, and clearance 
              fees are the customer's responsibility.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">8.2 Customs Documentation</h3>
            <p className="mb-4">
              We provide all necessary commercial invoices and customs documentation. Shipments may be delayed 
              by customs clearance processes beyond our control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Installation Services</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">9.1 Professional Installation</h3>
            <p className="mb-4">
              While delivery includes bringing the product to your location, professional installation by a 
              licensed plumber is required and sold separately. We strongly recommend professional installation 
              to ensure proper functionality and warranty coverage.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4">9.2 Installation Partner Network</h3>
            <p className="mb-4">
              We work with certified plumbers and installers across Europe. Contact us to arrange professional 
              installation in your area. Installation costs vary by location and complexity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Order Tracking</h2>
            <p className="mb-4">
              Track your order in real-time using the tracking link provided in your shipment notification email. 
              For assistance with tracking or delivery questions, contact us:
            </p>
            <ul className="space-y-2">
              <li><strong>Email:</strong> <a href="mailto:info@aureum7trading.com" className="text-primary hover:underline">info@aureum7trading.com</a></li>
              <li><strong>Phone:</strong> +34 642 278 939</li>
              <li><strong>WhatsApp:</strong> +34 642 278 939</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">11. Policy Updates</h2>
            <p>
              We reserve the right to update this Shipping & Delivery Policy at any time. Changes will be 
              effective immediately upon posting. Continued use of our services after changes constitutes 
              acceptance of the revised policy.
            </p>
          </section>

          <section className="border-t pt-6">
            <p className="text-sm text-muted-foreground">
              For questions about shipping, delivery, or to request a custom shipping quote, please contact 
              our customer service team at info@aureum7trading.com or +34 642 278 939.
            </p>
          </section>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default ShippingPolicy;
